import React from 'react';
import { Button } from 'react-bootstrap';

/**
 * PaginationControls component displays pagination UI
 * Shows item range, page info, and navigation buttons
 *
 * @param {object} pagination - Pagination state { offset, limit, total }
 * @param {Function} onNextPage - Handler for next page
 * @param {Function} onPreviousPage - Handler for previous page
 * @param {boolean} hasNextPage - Whether there is a next page
 * @param {boolean} hasPreviousPage - Whether there is a previous page
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 *
 * @example
 * <PaginationControls
 *   pagination={pagination}
 *   onNextPage={goToNextPage}
 *   onPreviousPage={goToPreviousPage}
 *   hasNextPage={hasNextPage}
 *   hasPreviousPage={hasPreviousPage}
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 * />
 */
export const PaginationControls = ({
  pagination,
  onNextPage,
  onPreviousPage,
  hasNextPage,
  hasPreviousPage,
  currentPage,
  totalPages,
}) => {
  const { offset, limit, total } = pagination;

  // Don't show pagination if there's only one page or no data
  if (total <= limit) return null;

  const startItem = offset + 1;
  const endItem = Math.min(offset + limit, total);

  return (
    <>
      {/* Item range display */}
      <div className="px-3 py-2 bg-light">
        <small className="text-muted">
          Showing {startItem} to {endItem} of {total} items
        </small>
      </div>

      {/* Pagination buttons */}
      <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
        <Button
          variant="secondary"
          onClick={onPreviousPage}
          disabled={!hasPreviousPage}
        >
          ← Previous
        </Button>
        <span className="text-muted">
          Page {currentPage} of {totalPages}
        </span>
        <Button
          variant="secondary"
          onClick={onNextPage}
          disabled={!hasNextPage}
        >
          Next →
        </Button>
      </div>
    </>
  );
};

export default PaginationControls;
